import type { AuthSession } from './types';
import { tokenFingerprint } from './token-fingerprint';

// Ephemeral same-origin rotation handoff. Never persisted or logged. A tab must
// already possess the predecessor token to accept a handoff (including cloned
// sessionStorage tabs). The existing activity channel carries no credentials.
const recent = new Map<string, { session: AuthSession; until: number }>();
let channel: BroadcastChannel | undefined;
const pending = new Map<string, (session: AuthSession) => void>();
const subscribers = new Set<(key: string, session: AuthSession) => void>();
async function advanceHandoffs(key: string, session: AuthSession) {
  for (const entry of recent.values()) {
    if (entry.until > Date.now() && await fingerprint(entry.session.tokens.refreshToken) === key)
      entry.session = session;
  }
}
export function watchRefresh(read: () => AuthSession | null, accept: (session: AuthSession) => void) {
  connect();
  subscribers.add((key, session) => {
    const previous = read();
    if (!previous || previous.user.id !== session.user.id) return;
    void fingerprint(previous.tokens.refreshToken).then((digest) => {
      if (digest === key && read()?.tokens.refreshToken === previous.tokens.refreshToken) accept(session);
    });
  });
}
async function fingerprint(token: string): Promise<string> {
  if (!crypto.subtle) return tokenFingerprint(token);
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  return Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
function connect() {
  if (channel || typeof BroadcastChannel === 'undefined') return;
  channel = new BroadcastChannel('ochal.auth.rotation.v1');
  channel.addEventListener('message', (event: MessageEvent<unknown>) => {
    const value = event.data as { type?: string; key?: string; session?: AuthSession } | null;
    if (!value || typeof value.key !== 'string' || !/^[a-f0-9]{64}$/.test(value.key)) return;
    if (value.type === 'request') {
      const cached = recent.get(value.key);
      if (cached && cached.until > Date.now())
        channel?.postMessage({ type: 'result', key: value.key, session: cached.session });
    } else if (
      value.type === 'result' &&
      value.session?.tokens?.refreshToken &&
      value.session.user?.id
    ) {
      pending.get(value.key)?.(value.session);
      subscribers.forEach((listener) => listener(value.key!, value.session!));
      void advanceHandoffs(value.key, value.session);
    }
  });
}
async function storageLock<T>(name: string, work: () => Promise<T>): Promise<T> {
  // Lamport bakery ordering, rather than a read-then-write single-key lease.
  // Only random lock IDs/tickets are stored; never credentials.
  const prefix = `ochal.auth.refresh-lock:${name}:`;
  const own = `${prefix}${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const expires = Date.now() + 120_000;
  const peers = () =>
    Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
      .filter((key): key is string => key !== null && key.startsWith(prefix))
      .map((key) => {
        try {
          return { key, ...JSON.parse(localStorage.getItem(key) ?? '{}') } as {
            key: string;
            choosing?: boolean;
            ticket?: number;
            expires?: number;
          };
        } catch {
          return { key };
        }
      })
      .filter((peer) => (peer.expires ?? 0) > Date.now());
  localStorage.setItem(own, JSON.stringify({ choosing: true, expires }));
  try {
    const ticket = 1 + Math.max(0, ...peers().map((peer) => peer.ticket ?? 0));
    localStorage.setItem(own, JSON.stringify({ choosing: false, ticket, expires }));
    const deadline = Date.now() + 20_000;
    while (
      peers().some(
        (peer) =>
          peer.key !== own &&
          (peer.choosing ||
            ((peer.ticket ?? 0) > 0 &&
              ((peer.ticket ?? 0) < ticket || (peer.ticket === ticket && peer.key < own)))),
      )
    ) {
      if (Date.now() >= deadline) throw new Error('SESSION_REFRESH_COORDINATION_TIMEOUT');
      await new Promise((resolve) => setTimeout(resolve, 25));
    }
    return await work();
  } finally {
    localStorage.removeItem(own);
  }
}
export async function coordinateRefresh(
  original: AuthSession,
  read: () => AuthSession | null,
  accept: (session: AuthSession) => void,
  rotate: (session: AuthSession) => Promise<AuthSession>,
): Promise<AuthSession | null> {
  connect();
  const key = await fingerprint(original.tokens.refreshToken);
  const work = async () => {
    const current = read();
    if (!current || current.user.id !== original.user.id) return null;
    if (current.tokens.refreshToken !== original.tokens.refreshToken) return current;
    for (const [id, value] of recent) if (value.until <= Date.now()) recent.delete(id);
    let cached = recent.get(key)?.session;
    if (!cached && channel) {
      cached = await new Promise<AuthSession | undefined>((resolve) => {
        const timer = setTimeout(() => {
          pending.delete(key);
          resolve(undefined);
        }, 150);
        pending.set(key, (session) => {
          clearTimeout(timer);
          pending.delete(key);
          resolve(session);
        });
        channel!.postMessage({ type: 'request', key });
      });
    }
    if (cached && cached.user.id === current.user.id) {
      accept(cached);
      return cached;
    }
    const next = await rotate(current);
    await advanceHandoffs(key, next);
    recent.set(key, { session: next, until: next.policy?.absoluteExpiresAt ?? next.tokens.expiresAt });
    // Keep sleeping/cloned sessionStorage tabs current, not only waiting callers.
    channel?.postMessage({ type: 'result', key, session: next });
    return next;
  };
  if (typeof navigator !== 'undefined' && navigator.locks)
    return navigator.locks.request(`ochal.auth.refresh:${original.user.id}`, work);
  return storageLock(original.user.id, work);
}
